import type {
  AppliedFilterOptionRange,
  Filters,
  SelectedFilterOption,
} from 'types/project'

// ---------------------------------------------------------------------------
// Canonical reviews-only `score` criterion, the score review-type detector, and
// the reviews-only request applier — the single source of truth for the charts ↔
// reviews-surface contract.
//
// WIRE SHAPE: the dashboard's Reviews/Ratings radio (SelectorBody) emits a
// `score` criterion `{field, value_type, min, max}` where `value_type` is
//   0 = Reviews & Ratings (both)   1 = Reviews Only   2 = Ratings Only
// and min/max is the 1-5 score-slider range. The backend converter maps
// `value_type` → `score_review_type`.
//
// Request-only defaults are deliberately NOT written into the shared
// `useFilterStore` and are not overlaid into the UI. The store travels between
// pages, so only user-selected score filters should appear there.
// ---------------------------------------------------------------------------

const SCORE_FIELD = 'score'
// The score slider's full-range default is 1-5 (SelectorBody) — "all scores".
const SCORE_MIN = 1
const SCORE_MAX = 5

/**
 * The `score` criterion's review-type, mirroring the backend's
 * `score_review_type`. The numeric values ARE the wire `value_type`.
 */
export enum ScoreReviewType {
  Both = 0,
  ReviewsOnly = 1,
  RatingsOnly = 2,
}

/**
 * Reviews Only across all scores — the request/export shape used when
 * reviews-driven surfaces need to scope data to reviews with text.
 */
export const reviewsOnlyCriterion: AppliedFilterOptionRange = {
  field: SCORE_FIELD,
  value_type: ScoreReviewType.ReviewsOnly,
  min: SCORE_MIN,
  max: SCORE_MAX,
}

const reviewsOnlyWithNullScoreCriterion: AppliedFilterOptionRange & {
  values: ['Null']
} = {
  ...reviewsOnlyCriterion,
  values: ['Null'],
}

type ScoreWithOptionalNullPreference = AppliedFilterOptionRange & {
  values?: string[]
}

const isScore = (value: { field: string }): boolean =>
  value.field === SCORE_FIELD

/**
 * A `score` criterion is well-formed when it carries a numeric `value_type`
 * (the range shape `{field, value_type, min, max}`). The malformed shape is the
 * select-shape `{field:'score', values:[]}` charts-v2 used to strip a range to
 * in the shared store — it has no `value_type` and 500s the backend's
 * range-constraint validator.
 */
const isWellFormedScore = (value: { field: string }): boolean =>
  isScore(value) &&
  typeof (value as Partial<AppliedFilterOptionRange>).value_type === 'number'

/**
 * The review-type a feedback surface must honor for the given filters. This is
 * the contract's charts→feedback column, expressed as a single value:
 *
 *   - a well-formed reviews-only (`value_type:1`) score  → `ReviewsOnly`
 *     (the user's range is preserved by `applyReviewsOnly`)
 *   - a well-formed both (`value_type:0`) score          → `Both`
 *     (feedback shows the unsupported screen; the explicit store score carries)
 *   - a well-formed ratings-only (`value_type:2`) score  → `RatingsOnly`
 *     (feedback shows the not-applicable screen; no request)
 *   - no score / a malformed `{values:[]}` score         → `ReviewsOnly`
 *     (request-only default; no UI filter is selected)
 *
 * Detection reads ONLY the passed filters; it never touches the shared store.
 */
export const detectScoreReviewType = (filters: Filters): ScoreReviewType => {
  const score = filters.values.find(isWellFormedScore) as
    | AppliedFilterOptionRange
    | undefined
  if (
    score?.value_type === ScoreReviewType.Both ||
    score?.value_type === ScoreReviewType.RatingsOnly
  ) {
    return score.value_type
  }
  return ScoreReviewType.ReviewsOnly
}

/**
 * Return a copy of `filters` whose `criteria` carry a well-formed reviews-only
 * `score` criterion, for the export boundary AND every feedback request
 * boundary (grid / summary / summarize). The contract's four charts→feedback
 * rows, derived from the carried-in `score`:
 *
 *   1. A well-formed REVIEWS-ONLY score (`value_type:1`, e.g. 1-3): the user's
 *      explicit reviews-only choice wins — RANGE PRESERVED, returned unchanged.
 *   2. A well-formed BOTH score (`value_type:0`, any range): feedback data
 *      stays reviews-only — the request copy uses reviews-only over that range.
 *   3. A MALFORMED `{field:'score', values:[]}` carried in from charts-v2: it
 *      is DROPPED (the backend 500s on it) and replaced with the canonical
 *      reviews-only criterion.
 *   4. No `score` criterion: the canonical reviews-only criterion is appended.
 *
 * Ratings-only (`value_type:2`) is NOT a request row — the feedback surface
 * renders the not-applicable screen and issues no request — so this applier is
 * not called for it. It is left untouched here for safety (idempotent).
 *
 * Returns the same `filters` reference only in case 1 (nothing changed).
 */
export const applyReviewsOnly = (filters: Filters): Filters => {
  const score = filters.values.find(isWellFormedScore) as
    | AppliedFilterOptionRange
    | undefined

  // Case 1: an explicit reviews-only range — preserve it verbatim.
  if (score?.value_type === ScoreReviewType.ReviewsOnly) return filters

  if (score?.value_type === ScoreReviewType.Both) {
    const withoutScore = filters.values.filter((value) => !isScore(value))
    return {
      ...filters,
      values: [
        ...withoutScore,
        {
          ...reviewsOnlyCriterion,
          min: score.min ?? SCORE_MIN,
          max: score.max ?? SCORE_MAX,
        },
      ],
    }
  }

  // Cases 3 (malformed) / 4 (absent): drop any existing malformed `score` and
  // force the canonical reviews-only criterion. Leaving a malformed one in
  // would still 500.
  const withoutScore = filters.values.filter((value) => !isScore(value))

  return {
    ...filters,
    values: [...withoutScore, reviewsOnlyCriterion],
  }
}

/**
 * Feedback grid/summary requests need the default score scope to mean
 * "reviews-only, with a score from 1-5 OR no score." Preserve a narrowed
 * explicit reviews-only range because that is a user score filter, not just the
 * feedback default.
 */
export const applyFeedbackRequestReviewsOnly = (filters: Filters): Filters => {
  const score = filters.values.find(isWellFormedScore) as
    | ScoreWithOptionalNullPreference
    | undefined

  if (score?.value_type === ScoreReviewType.ReviewsOnly) {
    if (score.values?.includes('Null')) return filters

    if (score.values) {
      const withoutScore = filters.values.filter((value) => !isScore(value))
      const { values: _values, ...scoreWithoutNullPreference } = score
      return {
        ...filters,
        values: [...withoutScore, scoreWithoutNullPreference],
      }
    }

    if (score.min !== SCORE_MIN || score.max !== SCORE_MAX) {
      return filters
    }
  }

  if (score?.value_type === ScoreReviewType.Both) {
    const withoutScore = filters.values.filter((value) => !isScore(value))
    return {
      ...filters,
      values: [
        ...withoutScore,
        {
          ...reviewsOnlyCriterion,
          min: score.min ?? SCORE_MIN,
          max: score.max ?? SCORE_MAX,
        },
      ],
    }
  }

  const withoutScore = filters.values.filter((value) => !isScore(value))
  return {
    ...filters,
    values: [...withoutScore, reviewsOnlyWithNullScoreCriterion],
  }
}

const reviewsOnlyFilterV2: SelectedFilterOption = {
  field: SCORE_FIELD,
  operator: 'is',
  values: [],
  min: SCORE_MIN,
  max: SCORE_MAX,
  valueType: ScoreReviewType.ReviewsOnly,
}

const isWellFormedScoreV2 = (filter: SelectedFilterOption): boolean =>
  filter.field === SCORE_FIELD && typeof filter.valueType === 'number'

/**
 * `applyFeedbackRequestReviewsOnly` for v2 panel filters — the same four rules,
 * applied to `panel.filters` (`{field, operator, values, min, max, valueType}`)
 * instead of v1 `Filters.values` (`{field, value_type, min, max}`). Feedback's
 * post-previews and summary-panel requests send a v2 `panel`, so this is the
 * copy that shapes those request bodies. Keep the two in step.
 *
 *   - well-formed reviews-only score, "Null" preference set  → unchanged
 *   - well-formed reviews-only score, narrowed range         → kept (any stray
 *     `values` cleared)
 *   - well-formed both-types score                           → reviews-only over
 *     that range (no "Null")
 *   - anything else (absent, malformed `values`-only score,
 *     full-range reviews-only without "Null")                → the score is
 *     replaced by the default: reviews-only 1-5 OR no score (`values:['Null']`)
 *
 * Returns the same array reference only when nothing changed.
 */
export const applyFeedbackRequestReviewsOnlyToFilters = <
  T extends SelectedFilterOption,
>(
  filters: T[],
): T[] => {
  const score = filters.find(isWellFormedScoreV2)
  const withoutScore = filters.filter((f) => f.field !== SCORE_FIELD)

  if (score?.valueType === ScoreReviewType.ReviewsOnly) {
    if (score.values.includes('Null')) return filters

    if (score.min !== SCORE_MIN || score.max !== SCORE_MAX) {
      return score.values.length === 0
        ? filters
        : [...withoutScore, { ...score, values: [] }]
    }
  }

  if (score?.valueType === ScoreReviewType.Both) {
    return [
      ...withoutScore,
      {
        ...reviewsOnlyFilterV2,
        min: score.min ?? SCORE_MIN,
        max: score.max ?? SCORE_MAX,
      } as T,
    ]
  }

  return [...withoutScore, { ...reviewsOnlyFilterV2, values: ['Null'] } as T]
}
